import React from "react";
import { Link } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";

import { H2, Paragraph, Bold, Span } from "../components/Typography/Typography";
import { FollowActions } from "../components/FollowActions/FollowActions";
import { LoadingSpinner } from "../components/LoadingSpinner/LoadingSpinner";
import { EmptyStateBox } from "../components/EmptyStateBox/EmptyStateBox";
import { useProfile } from "../contexts/ProfileContext";
import { ClerkUser } from "../api/clerk";
import { getPendingFollowRequests } from "../api/follows";

import "../components/Button/Button.scss";
import "./Friends.scss";

export function FriendsTab() {
  const { user, isLoaded } = useUser();
  const { userData, friends, isLoadingFriends, isCurrentUser, refreshFriends } = useProfile();

  const [isLoadingRequests, setIsLoadingRequests] = React.useState(true);
  const [pendingFollowRequests, setPendingFollowRequests] = React.useState<ClerkUser[]>([]);

  const loadPendingRequests = React.useCallback(async () => {
    if (isLoaded && isCurrentUser && user?.externalId) {
      setIsLoadingRequests(true);

      try {
        const requestsResponse = await getPendingFollowRequests(user.externalId);
        setPendingFollowRequests(requestsResponse);
      } catch (error) {
        console.error("Error loading friend requests:", error);
        setPendingFollowRequests([]);
      } finally {
        setIsLoadingRequests(false);
      }
    } else {
      setPendingFollowRequests([]);
      setIsLoadingRequests(false);
    }
  }, [isLoaded, isCurrentUser, user?.externalId]);

  const loadFriendsData = React.useCallback(async () => {
    await refreshFriends();
    await loadPendingRequests();
  }, [refreshFriends, loadPendingRequests]);

  React.useEffect(() => {
    loadPendingRequests();
  }, [loadPendingRequests]);

  let friendRequestsSection = null;
  if (isCurrentUser && !isLoadingRequests && pendingFollowRequests.length > 0) {
    friendRequestsSection = (
      <ul className="pending-requests">
        {pendingFollowRequests.map((request) => (
          <li key={request.id} className="follower-row">
            <img
              src={request.imageUrl}
              alt={request.username ?? "user"}
              width="45"
              height="45"
            />
            <Paragraph>
              <Bold>
                <Link to={`/${request.username}`}>@{request.username}</Link>
              </Bold>{" "}
              wants to trade albums with you
            </Paragraph>
            <FollowActions
              otherUserId={request.externalId}
              initialFollowStatus="pending"
              onStatusChange={loadFriendsData}
            />
          </li>
        ))}
      </ul>
    );
  }

  const isLoading = isLoadingFriends || (isCurrentUser && isLoadingRequests);
  const showSpinner = isLoading && friends.length === 0;
  const showEmptyState = !isLoading && friends.length === 0;

  return (
    <section className="friends-page__content" aria-label="Friends">
      {showSpinner && <LoadingSpinner />}

      {friendRequestsSection}

      {friends.length > 0 && (
        <ul className="friends-list">
          {friends.map((friend) => (
            <li key={friend.id} className="follower-row">
              <img
                src={friend.imageUrl}
                alt={friend.username ?? "user"}
                width="45"
                height="45"
              />
              <Paragraph>
                <Bold>
                  <Link to={`/${friend.username}`}>@{friend.username}</Link>
                </Bold>
              </Paragraph>
              {isCurrentUser && (
                <Link
                  className="button-component button-variant-outline-neutral button-size-medium"
                  to={`/${friend.username}`}
                >
                  View profile
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}

      {showEmptyState && (
        <EmptyStateBox>
          {isCurrentUser
            ? "No friends yet. Visit someone's profile and send them a friend request to start trading albums."
            : `@${userData.username} hasn't added any friends yet.`}
        </EmptyStateBox>
      )}
    </section>
  );
}
